import { Skeleton } from "design-system/atoms/Skeleton"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { cn } from "design-system/utils"
import { formatKnowledgeRate } from "modules/backoffice/lib/knowledge/formatKnowledgeRate"
import { useTranslation } from "react-i18next"
import { BackofficeKnowledgeMetricsResponseDataSchema } from "schema"
import { z } from "zod"
import { BackofficeKnowledgeSection } from "./BackofficeKnowledgeSection"

type Metrics = z.infer<typeof BackofficeKnowledgeMetricsResponseDataSchema>

type Props = {
  total: Metrics | null | undefined
  isPending: boolean
}

export function BackofficeKnowledgeAnswerSources({ total, isPending }: Props) {
  const { t } = useTranslation()

  const answeredCount = total?.answeredCount ?? 0

  const sources = [
    {
      key: "knowledgeBase",
      label: t("backoffice.knowledge.answerSources.knowledgeBase"),
      help: t("backoffice.knowledge.answerSources.knowledgeBaseHelp"),
      count: total?.answeredWithKnowledgeBaseCount ?? 0,
      className: "bg-chart-1",
    },
    {
      key: "capabilities",
      label: t("backoffice.knowledge.answerSources.capabilities"),
      help: t("backoffice.knowledge.answerSources.capabilitiesHelp"),
      count: total?.answeredWithCapabilityCount ?? 0,
      className: "bg-chart-3",
    },
    {
      key: "unattributed",
      label: t("backoffice.knowledge.answerSources.unattributed"),
      help: t("backoffice.knowledge.answerSources.unattributedHelp"),
      count: total?.answeredUnattributedCount ?? 0,
      className: "bg-muted-foreground/40",
    },
  ]

  function renderContent() {
    if (isPending) return <Skeleton className="h-3 w-full" />

    if (answeredCount === 0) {
      return (
        <p className="text-sm text-muted-foreground">
          {t("backoffice.knowledge.answerSources.empty")}
        </p>
      )
    }

    return (
      <>
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
          {sources.map((source) => (
            <div
              key={source.key}
              className={source.className}
              style={{ width: `${(source.count / answeredCount) * 100}%` }}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {sources.map((source) => (
            <Tooltip key={source.key}>
              <TooltipTrigger className="flex items-center gap-2 text-sm">
                <span className={cn("size-2.5 shrink-0 rounded-sm", source.className)} />
                <span>{source.label}</span>
                <span className="font-mono text-muted-foreground">
                  {t("backoffice.knowledge.answerSources.value", {
                    count: source.count,
                    rate: formatKnowledgeRate((source.count / answeredCount) * 100),
                  })}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-90">{source.help}</TooltipContent>
            </Tooltip>
          ))}
        </div>
      </>
    )
  }

  return (
    <BackofficeKnowledgeSection
      title={t("backoffice.knowledge.answerSources.title")}
      description={t("backoffice.knowledge.answerSources.description")}
    >
      <div className="flex flex-col gap-3 p-4">{renderContent()}</div>
    </BackofficeKnowledgeSection>
  )
}
